import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";

// Enum options extracted from Ruby code
export const categoryOptions = [
  { value: "1", label: "Investment Bank" },
  { value: "2", label: "Asset Manager" },
  { value: "3", label: "Private Bank" },
  { value: "4", label: "Family Office" },
  { value: "5", label: "Pension Fund" },
  { value: "6", label: "Sovereign Wealth Fund" },
  { value: "7", label: "Hedge Fund" },
  { value: "8", label: "Academic" },
  { value: "9", label: "Visitor" },
  { value: "10", label: "Insurance Company" },
  { value: "11", label: "Central Bank" },
  { value: "12", label: "Retail Bank" },
  { value: "13", label: "Fund Administration" },
  { value: "14", label: "Corporate" },
  { value: "15", label: "Research House" },
  { value: "16", label: "CGP" },
  { value: "17", label: "Consulting Firm" },
];

export const statusOptions = [
  { value: "0", label: "Pending" },
  { value: "1", label: "Approved" },
  { value: "2", label: "Blocked" },
];

export const roleOptions = [
  { value: "0", label: "Portfolio Manager" },
  { value: "1", label: "Research" },
  { value: "2", label: "Relationship Manager" },
  { value: "3", label: "Structuring" },
  { value: "4", label: "Marketing" },
  { value: "5", label: "Risk Management" },
  { value: "6", label: "Quantitative Analyst" },
  { value: "7", label: "Product Advisory" },
];

export const typeOptions = [
  { value: "User", label: "User" },
  { value: "AdminUser", label: "Admin User" },
];

export const filterUsersSchema = z.object({
  email_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  firstname_contains: z.string().nullable().optional(),
  lastname_contains: z.string().nullable().optional(),
  company_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  company_text_contains: z.string().nullable().optional(),
  group_user_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  category_contains: z.string().nullable().optional(),
  country_contains: z.string().nullable().optional(),
  role_contains: z.string().nullable().optional(),
  status_contains: z.string().nullable().optional(),
  type_contains: z.string().nullable().optional(),

  invitation_accepted_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  invitation_accepted_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  confirmed_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  confirmed_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  last_active_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  last_active_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  last_sign_in_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  last_sign_in_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterUsersSchema = z.infer<typeof filterUsersSchema>;

// All non-date fields first, followed by date fields at the end
export const fields: Field[] = [
  {
    name: "email_contains",
    label: "Email",
    type: "asyncAutocomplete",
    base_url: "admin-quantilia/users",
    column_name: "email",
  },
  {
    name: "firstname_contains",
    label: "First Name",
    type: "input",
  },
  {
    name: "lastname_contains",
    label: "Last Name",
    type: "input",
  },
  {
    name: "company_contains",
    label: "Company",
    type: "input",
  },
  {
    name: "company_text_contains",
    label: "Company Text",
    type: "input",
  },
  {
    name: "group_user_contains",
    label: "Group User",
    type: "asyncAutocomplete",
    base_url: "admin-quantilia/group_users",
    column_name: "name",
  },
  {
    name: "category_contains",
    label: "Category",
    type: "select",
    useTranslation: false,
    options: categoryOptions,
  },
  {
    name: "country_contains",
    label: "Country",
    type: "input",
  },
  {
    name: "role_contains",
    label: "Role",
    type: "select",
    useTranslation: false,
    options: roleOptions,
  },
  {
    name: "status_contains",
    label: "Status",
    type: "select",
    useTranslation: false,
    options: statusOptions,
  },
  {
    name: "type_contains",
    label: "Type",
    type: "select",
    useTranslation: false,
    options: typeOptions,
  },

  // Date fields (placed at the end)
  {
    name: "invitation_accepted_at_from",
    label: "Invitation Accepted From",
    type: "date",
  },
  {
    name: "invitation_accepted_at_to",
    label: "Invitation Accepted To",
    type: "date",
  },
  {
    name: "created_at_from",
    label: "Created From",
    type: "date",
  },
  {
    name: "created_at_to",
    label: "Created To",
    type: "date",
  },
  {
    name: "confirmed_at_from",
    label: "Confirmed From",
    type: "date",
  },
  {
    name: "confirmed_at_to",
    label: "Confirmed To",
    type: "date",
  },
  {
    name: "last_active_at_from",
    label: "Last Active From",
    type: "date",
  },
  {
    name: "last_active_at_to",
    label: "Last Active To",
    type: "date",
  },
  {
    name: "last_sign_in_at_from",
    label: "Last Sign In From",
    type: "date",
  },
  {
    name: "last_sign_in_at_to",
    label: "Last Sign In To",
    type: "date",
  },
];

export const client = "qtl - users";
export const tableName = "users";

export function FilterUsers() {
  return (
    <FilterTable
      client={client}
      schema={filterUsersSchema}
      tableName={tableName}
      customBuildPath={() => `${client}/users`}
      fields={fields}
      columns={2}
    />
  );
}
